import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BookOpen, User, Shield, Loader2, AlertCircle, ArrowRight } from 'lucide-react';
import { questionBankSharingApi } from '@/api/endpoints';
import { toast } from '@/utils/toast';

export const SharedQuestionBankPage = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const { data: response, isLoading, error } = useQuery({
    queryKey: ['resolve-share-link', token],
    queryFn: () => token ? questionBankSharingApi.resolveShareLink(token) : Promise.resolve(null),
    enabled: !!token,
    retry: false,
  });

  const linkData = response?.data?.data;

  useEffect(() => {
    if (linkData) {
      toast.success(`Access granted to "${linkData.bank_name}"`);
    }
  }, [linkData]);

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-white/[0.04] flex items-center justify-center mx-auto mb-4">
            <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
          </div>
          <p className="text-gray-400">Resolving share link...</p>
        </div>
      </div>
    );
  }

  if (error || !linkData) {
    const errorMessage = (error as any)?.response?.data?.error || 'This share link is invalid, expired, or has reached its maximum uses.';
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="max-w-md w-full">
          <div className="bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.06] rounded-xl p-8 text-center">
            <div className="w-16 h-16 rounded-2xl bg-red-500/10 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">Invalid Share Link</h2>
            <p className="text-gray-400 mb-6">{errorMessage}</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-2.5 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-xl font-medium shadow-lg shadow-teal-500/20 hover:shadow-teal-500/30 transition-all"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="max-w-lg w-full">
        <div className="bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.06] rounded-xl p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 rounded-2xl bg-teal-500/10 flex items-center justify-center mx-auto mb-4">
              <BookOpen className="w-8 h-8 text-teal-400" />
            </div>
            <h2 className="text-xl font-semibold text-white mb-1">Question Bank Shared With You</h2>
            <p className="text-gray-400 text-sm">You now have access to this question bank</p>
          </div>

          <div className="space-y-4 mb-6">
            <div className="bg-[#0d0d12] border border-white/[0.06] rounded-xl p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-teal-500/10 flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-teal-400" />
                </div>
                <div>
                  <h3 className="text-white font-medium">{linkData.bank_name}</h3>
                  {linkData.subject && (
                    <p className="text-gray-500 text-sm">{linkData.subject}</p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1.5 text-gray-400">
                  <Shield className="w-4 h-4" />
                  <span className="capitalize">{linkData.permission} access</span>
                </div>
                {linkData.owner_email && (
                  <div className="flex items-center gap-1.5 text-gray-400">
                    <User className="w-4 h-4" />
                    <span>Shared by {linkData.owner_email}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <button
            onClick={() => navigate(`/teacher/question-banks/${linkData.bank_id}`)}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-xl font-medium shadow-lg shadow-teal-500/20 hover:shadow-teal-500/30 transition-all"
          >
            Open Question Bank
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
